/**
 * "Start over" and the hold it can leave behind (spec
 * `docs/plans/2026-09-02-next-steps-e2e-encryption.md` section 6.7, screens 6
 * and 7; decision 16, "Start over needs proof or patience"; API contract
 * section 4 and the two `/reset` rows of section 6.6).
 *
 * The whole of what the Start over screen and the launch notice DO. Both are
 * renderings: every challenge, signature, wipe and dispatch is here, which is
 * what lets those screens be tested as pure renderings and this file be tested
 * without a component — the same split `device-approval.js` uses, and it reuses
 * that module's `signerFromStore` rather than building a third signer.
 *
 * TWO PATHS, AND THE DIFFERENCE IS PROOF, NOT INTENT:
 *
 *   this device holds MK  →  challenge, sign `reset`, POST  →  erased NOW,
 *                            then the key leaves this device and the platform
 *   no key at all         →  POST unsigned  →  a seven-day hold, which every
 *                            approved device is told about and can cancel
 *
 * ⚠️ THE UNSIGNED PATH IS NEVER A FALLBACK. A device that holds MK and fails to
 * sign reports the failure; it does not quietly post an unsigned reset. The two
 * outcomes are not variations on one action: one deletes the member's notes in
 * the next second, the other in seven days, and a member who typed the phrase
 * expecting the first must not be given the second without being told. (The
 * reverse is impossible: an unsigned POST cannot erase anything.)
 *
 * WHAT "CLEARS LOCAL STATE" HAS TO INCLUDE, and why each piece is load-bearing:
 *
 *   the platform copy   iOS Keychain / Android backup. If this survives, the
 *                       NEXT LAUNCH hydrates MK from it, `enrolIfEligible`
 *                       skips on ALREADY_HAS_KEY (enrolment.js:353) and the
 *                       member is left holding a key that opens nothing, with
 *                       no way to enrol again. So it is OVERWRITTEN with a
 *                       tombstone before it is deleted, and that write is the
 *                       one piece whose failure is reported rather than
 *                       swallowed (#1608; see `forgetKeysOnThisDevice`).
 *   the key record      IndexedDB, via `courses-keys/wipe`.
 *   the status snapshot `courses-devices/reset` — otherwise the store still
 *                       says `enrolled` for a bundle that no longer exists.
 *   the notes corpus    envelopes under a key nobody holds. Left behind, a
 *                       `complete` corpus of unopenable rows would greet the
 *                       next enrolment (decision 28).
 *   the read caches     `clearNextStepsCaches()`: the member's own words, of
 *                       rows the server has deleted. Every key the store
 *                       writes carries the `next-steps-` prefix, the five
 *                       content families of `PLAINTEXT_CACHE_PREFIXES`
 *                       included, so one sweep is all of them — and it is
 *                       reached through `utils/clear-next-steps-caches.js`
 *                       rather than through the courses store, which imports
 *                       this directory and would close a cycle.
 *
 * The enrolment banner key is deliberately NOT swept: `courses-e2ee-banner:<id>`
 * records that this member has already read the promise, and a re-enrolment
 * after Start over must not re-announce it (encryption-banner.js says so in as
 * many words).
 *
 * AND A RESET DOES NOT ALWAYS ARRIVE AS A TAP ON THIS DEVICE. The seven-day
 * hold completes LAZILY on the API's side (decision 16), on whatever request
 * happens to land first, so an approved device learns about it only from a
 * status read that comes back with no bundle. `reconcileLocalKeys` is that
 * arrival: a landed status saying there is no bundle — or a bundle under a
 * different `keyId` — while this device holds MK means the key it holds opens
 * nothing, and every one of the pieces above has to be cleared exactly as
 * though the member had tapped Start over here. Without it the reset the member
 * asked for never reaches the phone they were reading their notes on, and
 * `enrolIfEligible` skips for ever on ALREADY_HAS_KEY (enrolment.js:353): a
 * permanent dead end, reached with no failure at all, on every hold that
 * completes.
 */
import { clearNextStepsCaches } from '@/utils/clear-next-steps-caches';
import { notesCorpus } from '@/utils/courses-notes-corpus';
import { outbox } from '@/utils/courses-autosave/outbox';
import {
  DEVICE_STATUSES,
  API_ERROR_CODES,
  ENCRYPTION_ERROR_CODES,
  isEncryptionApiError,
} from '@/utils/courses-encryption-api';
import { APPROVAL_OUTCOME, adoptWrappedKey, signerFromStore } from './device-approval';
// Not through `loadKeyTools` below: `platform-slots` and `key-store` are pure
// JS — the versioned JSON both native adapters store, and the IndexedDB
// store — and import none of the native plugins the ADAPTER does. The lazy
// load is about the Keychain, not about these.
import { WIPE_SCOPE } from '@/utils/courses-crypto/key-store';
import { PLATFORM_COPY_SLOT, neutraliseSlot } from './platform-slots';

/** What one call here can end as. */
export const RESET_OUTCOME = Object.freeze({
  /** Proven: the bundle, every device and every row are gone, now. */
  ERASED: 'erased',
  /** A seven-day hold is running — started by this call, or already there. */
  PENDING: 'pending',
  /** The hold was called off with a signed `cancel`. */
  CANCELLED: 'cancelled',
  /** There is no bundle: nothing to reset, and nothing was sent. */
  NOT_ENROLLED: 'not-enrolled',
  /** `reason` says what. Nothing was deleted. */
  ERROR: 'error',
});

/** How long to wait for a hydration still in flight behind a deep link. */
export const HYDRATION_TIMEOUT_MS = 3000;

/**
 * The crypto and the platform adapter, loaded at the point of use.
 *
 * Lazy for the reason `enrolment.js` and `device-approval.js` are: the platform
 * key adapter statically imports two NATIVE plugins, and this module is
 * imported by a component App.vue mounts at boot. A static import would put the
 * Keychain behind every unit spec that mounts the app shell. Memoised, so a
 * second call in one session does not re-resolve the modules.
 *
 * @type {Promise<object> | null}
 */
let keyToolsPromise = null;

async function loadKeyTools() {
  if (!keyToolsPromise) {
    keyToolsPromise = Promise.all([import('@/utils/courses-crypto/platform-key-adapter')])
      .then(([adapter]) => ({ resolveAdapter: adapter.resolvePlatformAdapter }))
      .catch(error => {
        keyToolsPromise = null;
        throw error;
      });
  }
  return keyToolsPromise;
}

const DEFAULT_DEPS = Object.freeze({
  loadKeyTools,
  signerFromStore,
  adoptWrappedKey,
  clearNextStepsCaches,
  removeCorpus: memberId => notesCorpus.removeCorpus(memberId),
  removeOutbox: memberId => outbox.removeMember(memberId),
});

const withDeps = overrides => (overrides ? { ...DEFAULT_DEPS, ...overrides } : DEFAULT_DEPS);

const outcome = (value, extra = {}) => ({ outcome: value, ...extra });

/**
 * A plain, loggable failure. Deliberately not the error itself: an
 * `EncryptionApiError` carries the axios error as its `cause`, and the request
 * config — with the bearer token in it — inside that.
 *
 * @param {unknown} error
 */
function describeError(error) {
  if (isEncryptionApiError(error)) {
    const { code, apiCode, status, message, retryable } = error;
    return { code, apiCode, status, message, retryable };
  }
  return {
    code: 'unknown',
    apiCode: null,
    status: null,
    message: String(error?.message ?? error),
    retryable: false,
  };
}

const failed = (reason, error) =>
  outcome(RESET_OUTCOME.ERROR, { reason, ...(error ? { error: describeError(error) } : {}) });

/** A 409 that means "a hold is already running" rather than any other conflict. */
function isHoldAlreadyPending(error) {
  return (
    isEncryptionApiError(error, ENCRYPTION_ERROR_CODES.CONFLICT) &&
    error.apiCode === API_ERROR_CODES.RESET_ALREADY_PENDING
  );
}

/**
 * The member and their keys, read once the store is certainly hydrated.
 *
 * A deep link into the Start over screen can beat the hydration App.vue starts,
 * and a member whose keys have not been read yet is not a member without keys —
 * which on this screen is the difference between an immediate erase and a
 * seven-day wait.
 *
 * @param {object} store
 * @returns {Promise<{ memberId: number } | { reason: string }>}
 */
async function readyMember(store) {
  if (store.getters['courses-keys/isHydrated'] !== true) {
    try {
      await store.dispatch('courses-keys/ready', { timeoutMs: HYDRATION_TIMEOUT_MS });
    } catch (error) {
      return { reason: 'not-hydrated', error };
    }
  }
  const memberId = store.getters['courses-keys/memberId'];
  if (!Number.isInteger(memberId) || memberId <= 0) return { reason: 'no-member' };
  return { memberId };
}

/**
 * Forget this member's Next Steps key material and words on THIS device, after
 * a reset the server has already carried out.
 *
 * Ordered: the platform copy first, while `memberId` is still in the store, and
 * the local key record last, because `courses-keys/wipe` is what clears the id
 * the removal is keyed by.
 *
 * THE PLATFORM COPY IS OVERWRITTEN BEFORE IT IS REMOVED, and the write is not
 * gated on `isEligible()`. Both halves of that are the fix for #1608:
 *
 *   - A `remove` that throws used to leave the LIVE key in the Keychain or the
 *     Android backup. The next launch hydrates it, `enrolIfEligible` skips on
 *     ALREADY_HAS_KEY (enrolment.js:353), and the member holds a key for a
 *     bundle the server has deleted with no way to enrol again — a permanent
 *     dead end from a transient error, on the one flow that exists to give a
 *     locked-out member a way back. A tombstone that LANDED followed by a
 *     delete that failed is harmless: the slot parses as "no copy", so the
 *     hydration adopts nothing.
 *   - `isEligible()` is a CACHED probe, and any earlier throw in the session
 *     sets it false. Gating the write on it meant the removal was skipped
 *     entirely, no catch fired, and this function reported success over a live
 *     copy. The web adapter's `put` is a resolving no-op, so writing
 *     unconditionally costs the web build nothing.
 *
 * So there are three outcomes and only one of them is bad: overwrite ok and
 * remove ok is clean; overwrite ok and remove failed is ALSO clean; a failed
 * overwrite is the state the caller is told about.
 *
 * AND THEN HYDRATED AGAIN, which is not a flourish. `wipe` renews the
 * ready-gate (coursesKeys.js `renewGate`) and drops the cached hydration, so
 * the module is left IDLE with a fresh, unresolved gate — its own doc says
 * `ready` "while idle (no member yet, or just wiped) waits for the next one".
 * At logout that is right, because an Auth0 redirect follows. After a reset
 * nothing reloads: App.vue's only `hydrate` watcher is keyed on the profile id
 * and the token (App.vue's watcher), and a reset changes neither. So without
 * this the member is sent back to a Next Steps whose every annotation read
 * waits out HYDRATION_TIMEOUT_MS on `courses-keys/ready` and then renders
 * nothing, whose writes are refused for the same reason, and whose
 * `enrolIfEligible` returns NOT_HYDRATED rather than enrolling them afresh —
 * until the app is relaunched. Re-hydrating leaves the module in exactly the
 * state a relaunch would: this member, no key, gate settled.
 *
 * @param {{ store: object, memberId: number, deps?: object }} args
 * @returns {Promise<{ platformCopyNeutralised: boolean }>} whether the OVERWRITE
 *   resolved — not whether the removal did
 */
export async function forgetKeysOnThisDevice({ store, memberId, deps: overrides } = {}) {
  const deps = withDeps(overrides);

  // See the header: unconditional, and the overwrite is what has to land. A
  // failed overwrite is the one local failure worth reporting: a surviving
  // platform copy is hydrated on the next launch, and `enrolIfEligible` then
  // skips on ALREADY_HAS_KEY — a member holding a key that opens nothing,
  // unable to enrol again. The reset itself HAS happened, so this is not an
  // error outcome; it is a fact the caller is handed.
  //
  // THE COMMITTED SLOT ONLY. The provisional slot belongs to an enrolment
  // attempt, and this path knows only that the HELD key is dead — a stale copy
  // adopted at hydration can bring it here while an in-flight attempt holds
  // the only MK for the live bundle. Discarding an attempt is a decision only
  // the enrolment reconcile makes, with that attempt's own keyId in hand.
  let platformCopyNeutralised = false;
  try {
    const { resolveAdapter } = await deps.loadKeyTools();
    platformCopyNeutralised = await neutraliseSlot({
      adapter: resolveAdapter(),
      memberId,
      slot: PLATFORM_COPY_SLOT.COMMITTED,
    });
    // And the pending generation of a rotation this phone began, if any: a
    // forget that left MK2 behind would hand the next hydration half a key.
    await neutraliseSlot({ adapter: resolveAdapter(), memberId, slot: PLATFORM_COPY_SLOT.PENDING });
  } catch (error) {
    console.warn('[CoursesE2EE] The platform adapter could not be loaded:', error);
  }

  // Envelopes under a key nobody holds. Best effort: `removeCorpus` resolves
  // false where there is no IndexedDB, and a blocked delete is the logout
  // sweep's problem, not this screen's.
  try {
    await deps.removeCorpus(memberId);
  } catch (error) {
    console.warn('[CoursesE2EE] The notes corpus was not removed:', error);
  }

  // And anything still queued. A reset makes the old key unrecoverable, so an
  // entry sealed under it could never be sent OR opened again. Sign-out removes
  // the same queue for a different reason (plan 9.3): a member's words do not
  // stay on a phone they have signed out of.
  try {
    await deps.removeOutbox(memberId);
  } catch (error) {
    console.warn('[CoursesE2EE] The autosave outbox was not removed:', error);
  }

  // The member's own words, of rows the server has just deleted — the notes,
  // highlight and tag caches, the blank-answer map, and the reader view
  // preference, which is now a preference about notes that are gone.
  deps.clearNextStepsCaches();

  store.commit('courses/reset');
  store.commit('courses-devices/reset');
  try {
    // The key record only: an enrolment attempt in flight is not this path's
    // to discard (key-store.js, WIPE_SCOPE).
    await store.dispatch('courses-keys/wipe', { scope: WIPE_SCOPE.RESET });
  } catch (error) {
    // `wipe` commits `reset` before it touches storage, so the key is out of
    // memory whatever the store did; a record left in IndexedDB is removed by
    // the next sign-out.
    console.warn('[CoursesE2EE] The local key record was not removed:', error);
  }

  // See the header above. Never fatal: the reset itself has already happened,
  // and a hydration that fails leaves the same module state a failed hydration
  // at launch would.
  try {
    await store.dispatch('courses-keys/hydrate', { memberId });
  } catch (error) {
    console.warn('[CoursesE2EE] The keys were not re-hydrated after the reset:', error);
  }

  return { platformCopyNeutralised };
}

/**
 * A reset that reached this device without a tap on it: the API completed the
 * seven-day hold (decision 16, lazily, on some later request) and the status
 * now says there is no bundle — or a bundle under a `keyId` this device's key
 * was never wrapped for, which is a member who re-enrolled somewhere else after
 * the reset. Either way the key held here opens nothing.
 *
 * Takes the status the caller's read RESOLVED WITH rather than reading the
 * getters, because the getters keep the previous snapshot when a read fails, and
 * "the request did not land" must never be mistaken for "the bundle is gone".
 *
 * @param {{ store: object, status?: object|null, deps?: object }} args
 * @returns {Promise<{ reconciled: boolean, platformCopyNeutralised?: boolean }>}
 */
export async function reconcileLocalKeys({ store, status, deps: overrides } = {}) {
  if (!store) throw new TypeError('reconcileLocalKeys needs the root Vuex store');
  if (!status || typeof status !== 'object') return { reconciled: false };

  const material = store.getters['courses-keys/keyMaterial'];
  if (!material) return { reconciled: false };
  const memberId = store.getters['courses-keys/memberId'];
  if (!Number.isInteger(memberId) || memberId <= 0) return { reconciled: false };

  const bundleGone = status.enrolled !== true;

  /*
   * A `keyId` that has MOVED is the other way this device's key dies: the
   * member re-enrolled somewhere else after the reset, so there is a bundle —
   * just not the one this key was wrapped for.
   *
   * NOT WHILE A ROTATION IS PENDING (spec section 6.12, Phase 3). Two
   * generations legitimately coexist there: `keyId` is MK1 and `pendingKeyId`
   * is MK2, and the rotating device — the only one that can finish the job —
   * holds MK2, so its id differs from `status.keyId` by design. Wiping it would
   * strand every row already rewritten. Once the rotation commits, `keyId` is
   * the new generation and every other device is REVOKED with its `wrappedMk`
   * cleared, which is when a device holding MK1 alone does hold a dead key —
   * and decision 18 says a revoke that leaves MK on the device is a false
   * promise. So the mismatch is acted on only outside a rotation.
   *
   * Both ids have to be real strings either way: an absent one on either side
   * is a shape this client does not understand, and wiping a member's key on a
   * guess is the one mistake here that cannot be undone.
   */
  const serverKeyId = typeof status.keyId === 'string' ? status.keyId : null;
  const localKeyId = typeof material.keyId === 'string' ? material.keyId : null;

  /*
   * ⚠️ NOR IN THE GAP BETWEEN A COMMIT AND ITS PROMOTION, which `!status.rotation`
   * does not cover and which belongs to THIS device.
   *
   * `rotate/commit` clears the rotation server-side, and the store refreshes the
   * status as part of that call — but the local promotion is a separate await
   * afterwards (rotation.js says why it must come second). Between the two, the
   * server reports the NEW generation with no rotation running while this device
   * still holds MK1 as its committed key. Every term above is then satisfied and
   * a status read landing in that window wipes the keys of the very device that
   * just did the rotating, destroying the MK2 it had earned.
   *
   * The shape is already named elsewhere: `resumeStateFor` calls it PROMOTE — a
   * pending generation whose id IS the server's committed one. That is not a
   * bundle replaced somewhere else, it is this device a moment from finishing.
   *
   * Found as flakiness in COURSES-E2EE-014 (2026-09-09): the rotation completed
   * and `hasMasterKey` came back false on the retry that lost the race.
   */
  const held = store.getters['courses-keys/pendingKeyMaterial'];
  const midPromotion = !!serverKeyId && held?.keyId === serverKeyId;

  const keyIdMoved =
    !bundleGone &&
    !status.rotation &&
    !midPromotion &&
    !!serverKeyId &&
    !!localKeyId &&
    serverKeyId !== localKeyId;
  if (!bundleGone && !keyIdMoved) return { reconciled: false };

  console.warn(
    bundleGone
      ? '[CoursesE2EE] A reset has completed: forgetting the notes key on this device.'
      : '[CoursesE2EE] The bundle was replaced: forgetting the stale notes key on this device.',
  );
  /*
   * ⚠️ A DEVICE THE ROTATION KEPT RE-KEYS IN PLACE, AND NEVER FORGETS. The
   * forget below wipes the whole key record — the device identity with the
   * key — and a device with no identity sends no X-Device-Id, reads no
   * `thisDevice`, finds no wrapping waiting, and registers as a stranger.
   * That is exactly how the tablet a member chose to KEEP ended up on
   * "Approve this device" beside the phone they signed out (2026-09-11).
   * The status that brought us here already carries the wrapping the commit
   * left; the identity that opens it is still on this device; the only thing
   * to replace is the key.
   */
  if (!bundleGone && (await rekeyIfKept(store, status, overrides))) {
    return { reconciled: true, platformCopyNeutralised: false, rekeyed: true };
  }
  const { platformCopyNeutralised } = await forgetKeysOnThisDevice({
    store,
    memberId,
    deps: overrides,
  });
  return { reconciled: true, platformCopyNeutralised, rekeyed: false };
}

/**
 * A DEVICE THE ROTATION KEPT (api#1156): the status shows this device still
 * APPROVED, with MK2 wrapped to its public key. What the member wrote under
 * MK1 is cleared from the caches (the corpus is re-read under the new key),
 * and the wrapping is adopted over the old key — identity untouched, so the
 * member on this device never sees "Approve this device".
 *
 * @returns {Promise<boolean>} whether the new key was adopted; false hands the
 *   caller back to the ordinary forget
 */
async function rekeyIfKept(store, status, overrides) {
  const mine = status?.thisDevice;
  if (mine?.status !== DEVICE_STATUSES.APPROVED || !mine?.wrappedMk) return false;
  const deps = withDeps(overrides);
  const memberId = store.getters['courses-keys/memberId'];
  try {
    await deps.removeCorpus(memberId);
  } catch (error) {
    console.warn('[CoursesE2EE] The notes corpus was not removed before re-keying:', error);
  }
  deps.clearNextStepsCaches();
  store.commit('courses/reset');
  try {
    const adopted = await deps.adoptWrappedKey({ store });
    const rekeyed = adopted?.outcome === APPROVAL_OUTCOME.APPROVED;
    if (rekeyed) {
      console.warn('[CoursesE2EE] This device was kept through a sign-out: new key adopted.');
    } else {
      console.warn('[CoursesE2EE] The key this device was kept with was not adopted:', adopted);
    }
    return rekeyed;
  } catch (error) {
    console.warn('[CoursesE2EE] The key this device was kept with could not be adopted:', error);
    return false;
  }
}

/**
 * Screen 6. Two confirmations and the typed phrase have already happened; this
 * is what the tap does.
 *
 * Never rejects — the caller is a screen — so every answer, including every
 * failure, comes back as an outcome.
 *
 * @param {{ store: object, deps?: object }} args
 * @returns {Promise<{ outcome: string, reason?: string, error?: object, erased?: object,
 *   platformCopyNeutralised?: boolean }>}
 */
export async function startOver({ store, deps: overrides } = {}) {
  if (!store) throw new TypeError('startOver needs the root Vuex store');
  const deps = withDeps(overrides);

  const member = await readyMember(store);
  if (member.reason) return failed(member.reason, member.error);
  const { memberId } = member;

  // The API's own answer, never this device's guess: whether there is a bundle
  // at all, and whether somebody has already asked.
  let status;
  try {
    status = await store.dispatch('courses-devices/refreshStatus');
  } catch (error) {
    return failed('status-failed', error);
  }
  if (store.getters['courses-devices/enrolled'] !== true) {
    // There is nothing to reset — and if this device is nonetheless holding a
    // key, the reset has already happened (a hold that completed while the
    // member was on their way here). That key opens nothing, and left in place
    // it is what stops the Hub this screen sends them back to from ever
    // enrolling them again.
    //
    // `platformCopyNeutralised` is deliberately dropped here and in
    // `cancelReset` below: both are error branches of a screen that is about to
    // re-render from the status, with nowhere to put the sentence. The launch
    // watcher is where a member is told (reset-notice.js).
    await reconcileLocalKeys({ store, status, deps });
    return outcome(RESET_OUTCOME.NOT_ENROLLED);
  }
  // Already asked, from here or from another device. The unsigned POST would
  // answer 409 and a proven one would erase without the hold ever having been
  // seen; either way the member is looking at screen 7, not screen 6.
  if (store.getters['courses-devices/pendingReset']) return outcome(RESET_OUTCOME.PENDING);

  const hasKey = store.getters['courses-keys/hasMasterKey'] === true;
  if (!hasKey) return requestHold({ store });

  let signer;
  try {
    signer = await deps.signerFromStore({ store });
  } catch (error) {
    // NOT a fall-through to the unsigned hold: see the header. A device that
    // holds MK and cannot sign has a problem the member can retry, and turning
    // "now" into "in seven days" behind their back is not a retry.
    return failed('signer-failed', error);
  }

  let erased;
  try {
    erased = await store.dispatch('courses-devices/requestReset', { signer });
  } catch (error) {
    return failed('reset-failed', error);
  }

  const { platformCopyNeutralised } = await forgetKeysOnThisDevice({ store, memberId, deps });
  return outcome(RESET_OUTCOME.ERASED, {
    erased: erased?.erased ?? null,
    platformCopyNeutralised,
  });
}

/**
 * The unproven path: the member who has lost everything. No signature, so the
 * API starts the seven-day hold instead of erasing (decision 16).
 *
 * A 409 is not an error here. It means another device — or this one, on an
 * earlier open — has already asked, which is the state the member was trying to
 * reach; the spec's screen 7 is the answer either way, so the status is re-read
 * and the hold reported.
 *
 * @param {{ store: object }} args
 */
async function requestHold({ store }) {
  try {
    await store.dispatch('courses-devices/requestReset');
  } catch (error) {
    if (isHoldAlreadyPending(error)) {
      // `requestReset` rethrows before its own refresh runs, so the snapshot is
      // still the one without the hold. Screen 7 needs the dates.
      await store.dispatch('courses-devices/refreshStatus').catch(() => {});
      return outcome(RESET_OUTCOME.PENDING);
    }
    return failed('reset-failed', error);
  }
  return outcome(RESET_OUTCOME.PENDING);
}

/**
 * Screen 7's Cancel, from an approved device. Signed over `cancel` with the
 * bundle key, so a session thief cannot call off a genuine reset (decision 16;
 * the API answers 403 without one).
 *
 * @param {{ store: object, deps?: object }} args
 * @returns {Promise<{ outcome: string, reason?: string, error?: object }>}
 */
export async function cancelReset({ store, deps: overrides } = {}) {
  if (!store) throw new TypeError('cancelReset needs the root Vuex store');
  const deps = withDeps(overrides);

  const member = await readyMember(store);
  if (member.reason) return failed(member.reason, member.error);

  let signer;
  try {
    signer = await deps.signerFromStore({ store });
  } catch (error) {
    return failed('signer-failed', error);
  }

  try {
    await store.dispatch('courses-devices/cancelReset', { signer });
  } catch (error) {
    // 404 NO_RESET_PENDING: somebody cancelled it first, or the hold ran out
    // and the API completed it lazily. Nothing left to cancel either way, and
    // `cancelReset` refreshes the status only on success — so re-read it and
    // let the caller re-render against what is actually true.
    if (isEncryptionApiError(error, ENCRYPTION_ERROR_CODES.NOT_FOUND)) {
      const status = await store.dispatch('courses-devices/refreshStatus').catch(() => null);
      // The 404 may be the hold having RUN OUT rather than having been called
      // off: the API completes it on whatever request lands first, and this
      // cancel was that request. A landed status with no bundle says which, and
      // this device's key has to go with the notes it can no longer open.
      await reconcileLocalKeys({ store, status, deps });
      return failed('no-hold', error);
    }
    return failed('cancel-failed', error);
  }
  return outcome(RESET_OUTCOME.CANCELLED);
}

/** Specs reset the memoised adapter between cases; the app never calls this. */
export function resetStartOverStateForTests() {
  keyToolsPromise = null;
}
